// -------------- import internal dependencies ---------------
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

// ----------- import internal dependencies --------------
import {
  NewPasswordValidator,
  UpdatePasswordValidator,
} from "../../../utils/validators/auth";
//components
import AuthLayout from "../../../layouts/authLayout/AuthLayout";
// ----- import hooks -------
import useAuth from "../hooks/useAuth";
import {
  FormWrapper,
  FormHeaderText,
  FormSubTitle,
  Button,
} from "../styles/auth";
import FormInput from "../../../components/Forms/input/FormInput";
import { toast } from "react-toastify";
import {
  upperCaseReg,
  specialCharsReg,
  numberReg,
} from "../../../shared/sharedConstants";
import FormErrors from "../../../components/FormErrors";
import { clearStorage, useReportWrapper } from "../../../store/store";

const HandlePassword = () => {
  // ------- intialize custom hooks -------
  const { forgotUserPassword, changeDotNetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isLoading, mutateAsync: forgotPassword } = forgotUserPassword;
  const { isLoading: isNewPasswordLoading, mutateAsync: createPassword } =
    changeDotNetPassword;
  const email = searchParams.get("email");
  const email__re = email?.split(" ").join("+");
  const token = searchParams.get("token");
  const [password_criteria, setPassword_criteria] = useState({
    upperCase: false,
    number: false,
    specialChars: false,
    empty: false,
    eightChars: false,
  });
  const [isForgotPasswordPage, setIsForgotpasswordPage] = useState(false);

  useEffect(() => {
    if (location.pathname.includes("/forgot")) setIsForgotpasswordPage(true);
    else setIsForgotpasswordPage(false);
  }, [location]);

  const { handleSubmit, values, handleChange, handleBlur, errors } = useFormik({
    initialValues: {
      password: "",
      password__c: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: isForgotPasswordPage
      ? UpdatePasswordValidator()
      : NewPasswordValidator(),
    onSubmit: async (values: any) => {
      if (isForgotPasswordPage) {
        try {
          // eslint-disable-next-line
          const response = await forgotPassword({
            token: token,
            password: values.password,
            email: email__re,
          });
          navigate("/success?action=password");
        } catch (error: any) {
          console.log(error);
          toast.error(error.response.data?.message || "Verification Failed", {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 8000,
            toastId: "1",
          });
        }
      }

      if (!isForgotPasswordPage) {
        try {
          // Forced change goes to .NET. No oldPassword is sent because the endpoint does not
          // ask for one when the account is flagged for a forced change.
          // eslint-disable-next-line
          const response = await createPassword({
            newPassword: values.newPassword,
          });

          // The password change invalidates the .NET token, so tear down like logout.
          clearStorage();
          useReportWrapper.destroy();
          localStorage.clear();

          navigate("/success?action=password");
        } catch (error: any) {
          // eslint-disable-next-line no-console
          console.error("Password change failed", error?.response ?? error);

          // A 401 arrives with an empty body, so it needs its own message.
          const status = error.response?.status;

          const message =
            status === 401
              ? "Your session has expired. Please sign in again and retry."
              : error.response?.data?.error ||
                error.response?.data?.message ||
                "We could not change your password. Please try again.";

          toast.error(message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 8000,
            toastId: "1",
          });
        }
      }
    },
  });

  useEffect(() => {
    upperCaseReg.test(values.password)
      ? setPassword_criteria((prevState) => ({
          ...prevState,
          upperCase: true,
        }))
      : setPassword_criteria((prevState) => ({
          ...prevState,
          upperCase: false,
        }));

    specialCharsReg.test(values.password)
      ? setPassword_criteria((prevState) => ({
          ...prevState,
          specialChars: true,
        }))
      : setPassword_criteria((prevState) => ({
          ...prevState,
          specialChars: false,
        }));

    numberReg.test(values.password)
      ? setPassword_criteria((prevState) => ({
          ...prevState,
          number: true,
        }))
      : setPassword_criteria((prevState) => ({
          ...prevState,
          number: false,
        }));

    values.password !== ""
      ? setPassword_criteria((prevState) => ({ ...prevState, empty: true }))
      : setPassword_criteria((prevState) => ({
          ...prevState,
          empty: false,
        }));

    values.password.length >= 8
      ? setPassword_criteria((prevState) => ({
          ...prevState,
          eightChars: true,
        }))
      : setPassword_criteria((prevState) => ({
          ...prevState,
          eightChars: false,
        }));
  }, [values.password]);

  useEffect(() => {
    upperCaseReg.test(values.newPassword)
      ? setPassword_criteria((prevState) => ({
          ...prevState,
          upperCase: true,
        }))
      : setPassword_criteria((prevState) => ({
          ...prevState,
          upperCase: false,
        }));

    specialCharsReg.test(values.newPassword)
      ? setPassword_criteria((prevState) => ({
          ...prevState,
          specialChars: true,
        }))
      : setPassword_criteria((prevState) => ({
          ...prevState,
          specialChars: false,
        }));

    numberReg.test(values.newPassword)
      ? setPassword_criteria((prevState) => ({
          ...prevState,
          number: true,
        }))
      : setPassword_criteria((prevState) => ({
          ...prevState,
          number: false,
        }));

    values.newPassword !== ""
      ? setPassword_criteria((prevState) => ({ ...prevState, empty: true }))
      : setPassword_criteria((prevState) => ({
          ...prevState,
          empty: false,
        }));

    values.newPassword.length >= 8
      ? setPassword_criteria((prevState) => ({
          ...prevState,
          eightChars: true,
        }))
      : setPassword_criteria((prevState) => ({
          ...prevState,
          eightChars: false,
        }));
  }, [values.newPassword]);
  const handleDisableSubmitBtn = () => {
    const disableBtn =
      isLoading ||
      isNewPasswordLoading ||
      values.newPassword !== values.confirmPassword ||
      values.password !== values.password__c ||
      !password_criteria.empty ||
      !password_criteria.upperCase ||
      !password_criteria.number ||
      !password_criteria.specialChars ||
      !password_criteria.eightChars;
    return disableBtn;
  };
  return (
    <AuthLayout>
      <FormWrapper>
        <FormHeaderText>
          {isForgotPasswordPage ? "Forgot Password" : "New Password"}
        </FormHeaderText>
        <FormSubTitle>Please enter new password below. </FormSubTitle>
        <form onSubmit={handleSubmit}>
          {isForgotPasswordPage ? (
            <React.Fragment>
              <FormInput
                label={"New Password"}
                name="password"
                type="password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <FormInput
                label={"Confirm Password"}
                name="password__c"
                type="password"
                value={values.password__c}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <FormInput
                label={"New Password"}
                name="newPassword"
                type="password"
                value={values.newPassword}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <FormInput
                label={"Confirm Password"}
                name="confirmPassword"
                type="password"
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </React.Fragment>
          )}

          <FormErrors
            empty={password_criteria.empty}
            upperCase={password_criteria.upperCase}
            number={password_criteria.number}
            specialChars={password_criteria.specialChars}
            eightChars={password_criteria.eightChars}
            showMatch
            values={values}
            errors={errors}
            pwToCheck={"confirmPassword"}
          />

          <Button disabled={handleDisableSubmitBtn()}>
            {" "}
            {isLoading ? "Verifying..." : "Confirm"}{" "}
          </Button>

          <div className="mt-4 text-center text-sm font-medium">
            Don't have an account yet?{" "}
            <a
              href="https://mitigaterisk.com/contact-us/"
              target="_blank"
              rel="noreferrer"
              className="text-purple-300"
            >
              Contact us
            </a>
          </div>
        </form>
      </FormWrapper>
    </AuthLayout>
  );
};

export default HandlePassword;
